import axios from 'axios';
import DOMPurify from 'dompurify';

// Global cache for tooltip content
const tooltipCache = {};

// Track the currently visible tooltip
let currentTooltip = null;

// Initialize DOMPurify hooks once
let hooksInitialized = false;

function initializeDOMPurify() {
    if (hooksInitialized) return;

    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A') {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
        }

        if (node.tagName === 'IMG') {
            node.setAttribute('style', 'max-width: 100%; height: auto;');
            node.setAttribute('class', 'rich-text-image');
        }
    });

    hooksInitialized = true;
}

/**
 * Load tooltip content from API with caching
 * @param {string} identifier - Tooltip identifier
 * @returns {Promise<string>} - Sanitized HTML content
 */
export async function loadTooltipContent(identifier) {
    // Check cache first
    if (tooltipCache[identifier]) {
        return tooltipCache[identifier];
    }

    try {
        const response = await axios.get(`/api/tooltips/identifier/${identifier}`);
        const content = response.data.content || 'Tooltip content not available';

        // Sanitize and cache
        initializeDOMPurify();
        const sanitized = DOMPurify.sanitize(content, {
            ALLOWED_TAGS: ['a', 'p', 'strong', 'em', 'u', 's', 'blockquote', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'img'],
            ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style']
        });

        tooltipCache[identifier] = sanitized;
        return sanitized;
    } catch (err) {
        console.error('Error loading tooltip:', err);

        if (err.response && err.response.status === 404) {
            const fallback = 'Tooltip content is not yet available for this field.';
            tooltipCache[identifier] = fallback;
            return fallback;
        }

        return '<span style="color: #ef4444;">Failed to load information</span>';
    }
}

/**
 * Create and position tooltip element
 * @param {HTMLElement} anchorElement - Element to anchor tooltip to
 * @param {string} content - HTML content to display
 * @returns {HTMLElement} - Created tooltip element
 */
export function createTooltip(anchorElement, content) {
    if (!anchorElement) return null;

    // Remove any existing tooltip first to prevent overlapping
    removeCurrentTooltip();

    const isDark = document.documentElement.classList.contains('dark');

    // Create tooltip
    const tooltip = document.createElement('div');
    tooltip.className = 'info-icon-tooltip';
    if (isDark) {
        tooltip.classList.add('info-icon-tooltip--dark');
    }

    // Calculate position
    const rect = anchorElement.getBoundingClientRect();
    const tooltipWidth = 300;
    const offset = 8;

    let top = rect.bottom + offset;
    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);

    // Keep within viewport
    if (left < 10) left = 10;
    if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
    }
    if (top < 10) top = 10;

    // Apply styles
    Object.assign(tooltip.style, {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: '2147483647',
        pointerEvents: 'none',
        maxWidth: '300px',
        minWidth: '200px'
    });

    // Create content wrapper
    const contentDiv = document.createElement('div');
    contentDiv.className = 'info-icon-tooltip__content';
    contentDiv.innerHTML = content;

    tooltip.appendChild(contentDiv);
    document.body.appendChild(tooltip);

    // Store as current tooltip
    currentTooltip = tooltip;

    return tooltip;
}

/**
 * Remove tooltip element from DOM
 * @param {HTMLElement} tooltipElement - Tooltip element to remove
 */
export function removeTooltip(tooltipElement) {
    if (tooltipElement && tooltipElement.parentNode) {
        tooltipElement.parentNode.removeChild(tooltipElement);

        // Clear current tooltip reference if it matches
        if (currentTooltip === tooltipElement) {
            currentTooltip = null;
        }
    }
}

/**
 * Remove the currently visible tooltip
 */
function removeCurrentTooltip() {
    if (currentTooltip && currentTooltip.parentNode) {
        currentTooltip.parentNode.removeChild(currentTooltip);
        currentTooltip = null;
    }
}
